import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Button, CircularProgress } from '@mui/material';
import { DashboardContainer } from '../../Containers/DashboardLayout';
import { Toasts } from '../../Components/Toasts/Toasts';
import { useGetCountOfTable } from '../Users/saga/saga/saga';
import { clearCursorCache, fetchAllPosts, onUpdatePost } from './saga/saga/saga';

const PAGE_SIZE = 25;
const PLACEHOLDER_THUMB =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><rect width="48" height="48" fill="%23f3f4f6"/><text x="24" y="28" text-anchor="middle" font-size="10" fill="%239ca3af">no media</text></svg>';

export const Posts = () => {
  const [page, setPage] = useState(0);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const { count } = useGetCountOfTable('posts');

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const items = await fetchAllPosts({ page: p, docLimit: PAGE_SIZE });
      setList((prev) => (p === 0 ? items : [...prev, ...items]));
      if (items.length < PAGE_SIZE) setExhausted(true);
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(0);
    return () => clearCursorCache();
  }, [load]);

  const onLoadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  const setStatus = async (id, status) => {
    try {
      await onUpdatePost({ postId: id, data: { status } });
      setList((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
      Toasts.success(status === 'APPROVED' ? 'Approved' : 'Rejected');
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'update failed');
    }
  };

  return (
    <DashboardContainer>
      <div className="p-4">
        <h3 className="text-[22px] font-bold">
          Posts {count !== null && count > 0 && ` - ${count}`}
        </h3>

        <div className="mt-2 bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-left text-[14px]" style={{ minWidth: 900 }}>
            <thead className="bg-gray-50 text-gray-600 text-[12px] uppercase">
              <tr>
                <th className="px-3 py-2 whitespace-nowrap"></th>
                <th className="px-3 py-2 whitespace-nowrap">title</th>
                <th className="px-3 py-2 whitespace-nowrap">country</th>
                <th className="px-3 py-2 whitespace-nowrap">condition</th>
                <th className="px-3 py-2 whitespace-nowrap">price</th>
                <th className="px-3 py-2 whitespace-nowrap">posted</th>
                <th className="px-3 py-2 whitespace-nowrap">status</th>
                <th className="px-3 py-2 whitespace-nowrap">actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && !loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                    No posts yet.
                  </td>
                </tr>
              ) : (
                list.map((p) => {
                  const thumb = p.videos?.[0] || p.images?.[0] || PLACEHOLDER_THUMB;
                  const isVideo = !!p.videos?.[0];
                  return (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isVideo ? (
                          <video
                            src={thumb}
                            muted
                            playsInline
                            className="w-12 h-12 object-cover rounded bg-gray-100"
                          />
                        ) : (
                          <img
                            src={thumb}
                            alt=""
                            className="w-12 h-12 object-cover rounded bg-gray-100"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 max-w-[280px] truncate">
                        {p.title || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600 uppercase text-[12px]">
                        {p.country || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                        {p.condition || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {p.price != null ? (
                          <>
                            {p.currencyCode?.toUpperCase()} {p.price}
                            {p.discount ? (
                              <span className="text-gray-400 line-through ml-1">
                                {p.discount}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                        {moment(p.createdAt).fromNow()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {p.status === 'APPROVED' ? (
                          <span className="text-green-700">approved</span>
                        ) : p.status === 'REJECTED' ? (
                          <span className="text-red-600">rejected</span>
                        ) : (
                          <span className="text-yellow-700">pending</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex gap-1">
                          {p.status === 'APPROVED' ? (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => setStatus(p.id, 'REJECTED')}
                            >
                              Reject
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="success"
                              onClick={() => setStatus(p.id, 'APPROVED')}
                            >
                              Approve
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-center mt-4">
          {loading ? (
            <CircularProgress size={20} />
          ) : exhausted ? (
            <span className="text-gray-400 text-[13px]">No more rows.</span>
          ) : (
            <Button onClick={onLoadMore} variant="outlined" size="small">
              Load more
            </Button>
          )}
        </div>
      </div>
    </DashboardContainer>
  );
};
